import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MapsIcon from "../../assets/images/marker-icon.svg";
import { getAddressesByUserIdAsync } from "../../Admin-Api/GetAddressesByUserIdAsync.ts";
import { AddressData } from "../../API/Address/GetAddressesByUserIdAsync";

interface LocationState {
  userName?: string;
  email?: string;
}

const UserDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams<{ userId: string }>();
  const [addressData, setAddressData] = useState<AddressData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const state = location.state as LocationState | null;
  const userName = state?.userName || "N/A";
  const email = state?.email || "N/A";

  useEffect(() => {
    if (!userId) {
      toast.error("User ID not found.");
      navigate("/manageuser");
    } else {
      fetchAddressData();
    }
  }, [userId, navigate]);

  const fetchAddressData = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        if (!toast.isActive("auth-error")) {
          toast.error("No auth token found", { toastId: "auth-error" });
        }
        throw new Error("No auth token found");
      }

      const data: AddressData[] = await getAddressesByUserIdAsync(userId!, token);
      setAddressData(data);

      if (data.length === 0) {
        if (!toast.isActive("no-address")) {
          toast.info("No addresses found for this user.", { toastId: "no-address" });
        }
      }
    } catch (error) {
      if (!toast.isActive("generic-error")) {
        toast.error("An error occurred while fetching address data.", { toastId: "generic-error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleMapClick = (latitude: string, longitude: string) => {
    window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, "_blank");
  };

  const handleBack = () => {
    sessionStorage.setItem("fromUserDetails", "true");
    navigate("/manageuser");
  };

  return (
    <div className="min-h-screen overflow-hidden bg-magnolia">
      <div className="flex flex-col min-h-screen">
        <header className="flex items-center justify-between px-4 sm:px-6 py-3 bg-white">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="text-black-600 text-2xl font-bold hover:text-blue-700 leading-none"
              aria-label="Back"
            >
              &lt;
            </button>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-700">User Details</h1>
          </div>
        </header>

        <main className="flex-1 overflow-auto px-4 sm:px-6 py-6">
          <div className="container mx-auto max-w-screen-xl">


            <div className="mt-4 p-5 bg-white rounded-lg shadow-md">
              <h4 className="text-lg font-semibold mb-4">User Information</h4>

              <div className="space-y-2 text-sm text-gray-700">
                <p>
                  <span className="font-semibold text-gray-600">User Name: </span>
                  <span className="text-blue-600">{userName}</span>
                </p>
                <p>
                  <span className="font-semibold text-gray-600">Email: </span>
                  <span className="text-blue-600 break-all">{email}</span>
                </p>
              </div>
            </div>
            <br />
            <h4 className="text-base sm:text-lg text-dark font-semibold">User Address Details</h4>

            {isLoading && <p className="mt-4 text-gray-500">Loading data...</p>}

            {addressData.length > 0 && (
              <div className="mt-6">
                <h4 className="text-lg font-semibold">Addresses</h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
                  {addressData.map((address) => (
                    <div
                      key={address.id}
                      className="border rounded-lg p-5 bg-white shadow-sm hover:shadow-lg transition duration-200"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <h2 className="font-semibold text-gray-700">
                          {address.addressType || "Address"}
                        </h2>

                        <button
                          onClick={() => handleMapClick(address.latitude, address.longitude)}
                          title="View on Map"
                          className="flex items-center text-blue-600 hover:text-blue-800"
                        >
                          <img src={MapsIcon} alt="Map Icon" className="w-6 h-6" />
                        </button>
                      </div>

                      <div className="space-y-2 text-sm text-gray-600">
                        <div>
                          <span className="font-medium text-black-500">Address:</span>
                          <p className="text-black-600 break-words">{address.addressLine1},  {address.addressLine2 || "-"},  {address.addressLine3 || "-"}</p>
                        </div>

                        {/* <div>
                          {/* <span className="font-medium text-gray-500">Address Line 2:</span> */}
                        {/* <p className="text-blue-600 break-words">{address.addressLine2 || "-"}</p>
                        </div> */}

                        {/* <div> */}
                        {/* <span className="font-medium text-gray-500">Address Line 3:</span> */}
                        {/* <p className="text-blue-600 break-words">{address.addressLine3 || "-"}</p>
                        </div> */}

                        <div>
                          <span className="font-medium text-black-500">PAM Code:</span>
                          <p className="text-blue-600">
                            {`${address.pinCode}-${address.blockCode}-${address.addressCode}`}
                          </p>
                        </div>

                        <div>
                          <span className="font-medium text-black-500">Created Date:</span>
                          <p>{address.createdDate}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>

        <ToastContainer />
      </div>
    </div>
  );
};

export default UserDetails;